import { useContext } from "react";
import { useParams } from "react-router-dom";
import { AppContext } from "../../../store";
import { useDebounce } from "use-debounce";
import { useFutureCostQueries } from "../../FutureCost";

export const useCostChangeHistory = () => {
  const { id } = useParams();

  // Accessing global app context
  const {
    setTableState,
    tableData,
    clearAllFilters,
    setSorting,
    setPagination,
    setSearchState,
  } = useContext(AppContext);

  // Module name for future costs
  const moduleName = "costChangeHistory";
  // Current table state
  const tableState = tableData[moduleName];

  // Debounced search value
  const [debouncedSearch] = useDebounce(tableState.search, 1000);

  // Function to get list params based on table state
  const getListParams = (state) => {
    const params = {};
    // Apply pagination
    if (state.pagination) {
      const { pageIndex, pageSize } = state.pagination;
      params.page = pageIndex;
      params.per_page = pageSize;
    }

    // Apply search filter
    if (state?.search) params.search = state.search;

    // Apply sorting filter
    if (state?.sorting?.length > 0) {
      const sortByParams = state.sorting.reduce(
        (obj, item) =>
          Object.assign(obj, { [item.id]: item.desc ? "desc" : "asc" }),
        {},
      );
      params.sort = sortByParams;
    }

    return params;
  };

  // Fetching future cost history list
  const { costChangeHistoryQuery } = useFutureCostQueries({
    costChangeHistory: true,
    ingredientId: id,
    getHistoryListParamsCallback: getListParams.bind(null, {
      ...tableState,
      search: debouncedSearch,
    }),
  });

  // Handler for search input changes
  const onSearchHandler = (search) => {
    setSearchState({
      tableName: moduleName,
      key: "search",
      value: search,
    });
    setPagination({
      tableName: moduleName,
      callback: (prevPagination) => ({
        ...prevPagination,
        pageIndex: 1, // Reset to the first page
      }),
    });
  };

  return {
    costChangeHistoryQuery,
    tableState,
    moduleName,
    tableHelpers: {
      setPagination,
      setSorting,
      setSearchState,
      clearAllFilters,
      setTableState,
      onSearchHandler,
    },
  };
};
